import React, { useState, useEffect, useCallback } from 'react'
import {
  Box, Paper, Typography, Skeleton, useTheme, alpha,
  TablePagination, Drawer, IconButton, Chip, Button,
  ToggleButtonGroup, ToggleButton,
} from '@mui/material'
import {
  Shield, AlertTriangle, Ban, FileSearch, X, Monitor,
  RefreshCw, CheckCircle,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { complianceApi } from '../../../../api/complianceApi'

const SEVERITY_COLORS = {
  warning: '#f59e0b',
  suspension: '#f97316',
  termination: '#ef4444',
}

const SecurityTab = ({ period }) => {
  const { t } = useTranslation()
  const theme = useTheme()
  const goldColor = theme.palette.primary.main
  const isDark = theme.palette.mode === 'dark'

  const [overview, setOverview] = useState(null)
  const [overviewLoading, setOverviewLoading] = useState(true)
  const [subTab, setSubTab] = useState('license')

  // License violations state
  const [violations, setViolations] = useState([])
  const [violationsTotal, setViolationsTotal] = useState(0)
  const [violationsLoading, setViolationsLoading] = useState(true)
  const [violationsPage, setViolationsPage] = useState(0)

  // Document access state
  const [topUsers, setTopUsers] = useState([])
  const [docStats, setDocStats] = useState(null)
  const [docLoading, setDocLoading] = useState(true)

  // Drawer state
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [selectedUser, setSelectedUser] = useState(null)
  const [userCompliance, setUserCompliance] = useState(null)
  const [complianceLoading, setComplianceLoading] = useState(false)

  // Scoring
  const [scoringInProgress, setScoringInProgress] = useState(false)

  // Fetch overview
  const fetchOverview = useCallback(async () => {
    setOverviewLoading(true)
    try {
      const res = await complianceApi.getOverview()
      setOverview(res.data)
    } catch (err) {
      console.error('Failed to fetch compliance overview:', err)
    } finally {
      setOverviewLoading(false)
    }
  }, [])

  // Fetch violations
  const fetchViolations = useCallback(async () => {
    setViolationsLoading(true)
    try {
      const periodMap = { '7d': '7d', '30d': '30d', '90d': '90d' }
      const p = periodMap[period] || '30d'
      const res = await complianceApi.getViolations(p, '', violationsPage + 1, 50)
      setViolations(res.data?.violations || [])
      setViolationsTotal(res.data?.total || 0)
    } catch (err) {
      console.error('Failed to fetch violations:', err)
    } finally {
      setViolationsLoading(false)
    }
  }, [period, violationsPage])

  // Fetch doc access
  const fetchDocAccess = useCallback(async () => {
    setDocLoading(true)
    try {
      const [usersRes, statsRes] = await Promise.all([
        complianceApi.getTopDocAccessUsers(period === '7d' ? '7d' : '24h', 20),
        complianceApi.getDocAccessStats(),
      ])
      setTopUsers(usersRes.data?.users || [])
      setDocStats(statsRes.data)
    } catch (err) {
      console.error('Failed to fetch doc access:', err)
    } finally {
      setDocLoading(false)
    }
  }, [period])

  useEffect(() => { fetchOverview() }, [fetchOverview])
  useEffect(() => { if (subTab === 'license') fetchViolations() }, [fetchViolations, subTab])
  useEffect(() => { if (subTab === 'docaccess') fetchDocAccess() }, [fetchDocAccess, subTab])
  useEffect(() => { setViolationsPage(0) }, [period])

  const handleUserClick = async (userId) => {
    setDrawerOpen(true)
    setComplianceLoading(true)
    try {
      const res = await complianceApi.getUserCompliance(userId)
      setUserCompliance(res.data)
      setSelectedUser(res.data?.user)
    } catch (err) {
      console.error('Failed to fetch user compliance:', err)
    } finally {
      setComplianceLoading(false)
    }
  }

  const handleResolve = async (userId, violationId) => {
    try {
      await complianceApi.resolveViolation(userId, violationId)
      fetchViolations()
      if (drawerOpen) handleUserClick(userId)
    } catch (err) {
      console.error('Failed to resolve violation:', err)
    }
  }

  const handleUnsuspend = async (userId) => {
    try {
      await complianceApi.unsuspendUser(userId)
      fetchOverview()
      if (drawerOpen) handleUserClick(userId)
    } catch (err) {
      console.error('Failed to unsuspend user:', err)
    }
  }

  const handleTriggerScoring = async () => {
    setScoringInProgress(true)
    try {
      await complianceApi.triggerScoring()
      fetchOverview()
      fetchViolations()
    } catch (err) {
      console.error('Failed to trigger scoring:', err)
    } finally {
      setScoringInProgress(false)
    }
  }

  // Score bar component
  const ScoreBar = ({ label, value, max = 40, color }) => (
    <Box sx={{ mb: 1.5 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
        <Typography variant="caption" sx={{ fontSize: '0.72rem', color: theme.palette.text.secondary }}>{label}</Typography>
        <Typography variant="caption" sx={{ fontSize: '0.72rem', fontWeight: 600 }}>{value}</Typography>
      </Box>
      <Box sx={{ height: 6, borderRadius: 3, background: alpha(theme.palette.text.primary, 0.08) }}>
        <Box sx={{ height: '100%', borderRadius: 3, width: `${Math.min((value / max) * 100, 100)}%`, background: color || goldColor, transition: 'width 0.3s' }} />
      </Box>
    </Box>
  )

  const overviewCards = [
    { label: t('analytics_active_violations'), value: overview?.active_violations, icon: AlertTriangle, color: '#f59e0b' },
    { label: t('analytics_suspended'), value: overview?.suspended_count, icon: Ban, color: '#ef4444' },
    { label: t('analytics_flagged_users'), value: overview?.flagged_count, icon: Shield, color: '#f97316' },
    { label: t('analytics_total_violations'), value: overview?.total_violations, icon: FileSearch, color: theme.palette.text.secondary },
  ]

  const violationHeaders = [
    { label: t('analytics_email'), flex: 2 },
    { label: t('analytics_organization'), flex: 1.5 },
    { label: t('analytics_type'), flex: 1 },
    { label: t('analytics_score'), width: 70, align: 'right' },
    { label: t('analytics_action'), width: 120 },
    { label: t('analytics_date'), width: 120 },
  ]

  const docHeaders = [
    { label: t('analytics_email'), flex: 2 },
    { label: t('analytics_organization'), flex: 1.5 },
    { label: t('analytics_docs_accessed'), width: 110, align: 'right' },
    { label: t('analytics_last_access'), width: 140 },
  ]

  return (
    <Box sx={{ overflow: 'auto', '&::-webkit-scrollbar': { display: 'none' }, scrollbarWidth: 'none' }}>
      {/* Overview Cards */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' }, gap: 2, mb: 3 }}>
        {overviewCards.map((card) => {
          const Icon = card.icon
          return (
            <Paper
              key={card.label}
              elevation={0}
              sx={{ p: 2.5, borderRadius: '12px', border: `1px solid ${theme.palette.divider}` }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Box sx={{ width: 36, height: 36, borderRadius: '8px', background: alpha(card.color, 0.1), display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Icon size={18} style={{ color: card.color }} />
                </Box>
                <Box>
                  {overviewLoading ? (
                    <Skeleton width={40} height={28} />
                  ) : (
                    <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1.2rem', lineHeight: 1 }}>
                      {card.value ?? 0}
                    </Typography>
                  )}
                  <Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontSize: '0.7rem' }}>
                    {card.label}
                  </Typography>
                </Box>
              </Box>
            </Paper>
          )
        })}
      </Box>

      {/* Sub-tab toggle + Run Scoring button */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <ToggleButtonGroup
          value={subTab}
          exclusive
          onChange={(_, val) => val && setSubTab(val)}
          size="small"
          sx={{
            gap: 1,
            '& .MuiToggleButton-root': {
              borderRadius: '8px', textTransform: 'none', fontSize: '0.78rem', fontWeight: 600, px: 2, py: 0.75,
              border: `1px solid ${theme.palette.divider}`, color: theme.palette.text.secondary,
              '&.Mui-selected': { background: alpha(goldColor, isDark ? 0.12 : 0.08), color: goldColor, borderColor: alpha(goldColor, 0.3) },
            },
          }}
        >
          <ToggleButton value="license">
            <Shield size={14} style={{ marginRight: 6 }} /> {t('analytics_license_violations')}
          </ToggleButton>
          <ToggleButton value="docaccess">
            <FileSearch size={14} style={{ marginRight: 6 }} /> {t('analytics_document_access')}
          </ToggleButton>
        </ToggleButtonGroup>

        <Button
          size="small"
          variant="outlined"
          onClick={handleTriggerScoring}
          disabled={scoringInProgress}
          startIcon={<RefreshCw size={14} className={scoringInProgress ? 'animate-spin' : ''} />}
          sx={{
            borderRadius: '8px', textTransform: 'none', fontSize: '0.78rem', fontWeight: 600,
            borderColor: theme.palette.divider, color: theme.palette.text.secondary,
          }}
        >
          {scoringInProgress ? t('analytics_scoring_running') : t('analytics_run_scoring')}
        </Button>
      </Box>

      {/* License Violations Table */}
      {subTab === 'license' && (
        <Paper elevation={0} sx={{ borderRadius: '12px', border: `1px solid ${theme.palette.divider}`, overflow: 'hidden' }}>
          {/* Header */}
          <Box sx={{ display: 'flex', py: 1.2, px: 2, background: alpha(theme.palette.text.primary, 0.03), borderBottom: `1px solid ${theme.palette.divider}` }}>
            {violationHeaders.map((cell) => (
              <Box key={cell.label} sx={{ flex: cell.flex, width: cell.width, textAlign: cell.align || 'left', flexShrink: cell.width ? 0 : undefined }}>
                <Typography variant="caption" sx={{ fontWeight: 600, color: theme.palette.text.secondary, textTransform: 'uppercase', fontSize: '0.68rem', letterSpacing: '0.05em' }}>
                  {cell.label}
                </Typography>
              </Box>
            ))}
          </Box>

          {/* Body */}
          {violationsLoading ? (
            <Box sx={{ p: 2 }}>
              {[...Array(6)].map((_, i) => (
                <Skeleton key={i} variant="text" width="100%" height={40} sx={{ mb: 0.5 }} />
              ))}
            </Box>
          ) : violations.length === 0 ? (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>{t('analytics_no_violations')}</Typography>
            </Box>
          ) : (
            violations.map((v, idx) => (
              <Box
                key={v.id}
                onClick={() => handleUserClick(v.user_id)}
                sx={{
                  display: 'flex', py: 1.4, px: 2, cursor: 'pointer', alignItems: 'center',
                  borderBottom: idx < violations.length - 1 ? `1px solid ${alpha(theme.palette.divider, 0.5)}` : 'none',
                  transition: 'background 0.15s', '&:hover': { background: alpha(goldColor, 0.04) },
                }}
              >
                <Box sx={{ flex: 2, minWidth: 0 }}>
                  <Typography variant="body2" sx={{ fontSize: '0.82rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {v.email}
                  </Typography>
                </Box>
                <Box sx={{ flex: 1.5, minWidth: 0 }}>
                  <Typography variant="body2" sx={{ fontSize: '0.78rem', color: theme.palette.text.secondary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {v.organization || '-'}
                  </Typography>
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Chip
                    label={v.severity}
                    size="small"
                    sx={{
                      height: 22, fontSize: '0.7rem', fontWeight: 600,
                      background: alpha(SEVERITY_COLORS[v.severity] || goldColor, 0.12),
                      color: SEVERITY_COLORS[v.severity] || goldColor,
                      border: 'none',
                    }}
                  />
                </Box>
                <Box sx={{ width: 70, flexShrink: 0, textAlign: 'right' }}>
                  <Typography variant="body2" sx={{ fontSize: '0.82rem', fontWeight: 600 }}>{v.score}</Typography>
                </Box>
                <Box sx={{ width: 120, flexShrink: 0 }}>
                  <Typography variant="body2" sx={{ fontSize: '0.72rem', color: theme.palette.text.secondary }}>
                    {v.action_taken?.replace(/_/g, ' ')}
                  </Typography>
                </Box>
                <Box sx={{ width: 120, flexShrink: 0 }}>
                  <Typography variant="body2" sx={{ fontSize: '0.72rem', color: theme.palette.text.secondary }}>
                    {v.created_at ? new Date(v.created_at).toLocaleDateString() : '-'}
                  </Typography>
                </Box>
              </Box>
            ))
          )}

          {!violationsLoading && violationsTotal > 0 && (
            <TablePagination
              component="div"
              count={violationsTotal}
              page={violationsPage}
              onPageChange={(_, newPage) => setViolationsPage(newPage)}
              rowsPerPage={50}
              rowsPerPageOptions={[50]}
              sx={{ borderTop: `1px solid ${theme.palette.divider}`, '& .MuiTablePagination-toolbar': { minHeight: 48 } }}
            />
          )}
        </Paper>
      )}

      {/* Document Access Table */}
      {subTab === 'docaccess' && (
        <>
          {/* Doc stats row */}
          {docStats && (
            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
              {[
                { label: t('analytics_views_24h'), value: docStats.total_24h },
                { label: t('analytics_views_7d'), value: docStats.total_7d },
                { label: t('analytics_unique_users_24h'), value: docStats.unique_users_24h },
              ].map((s) => (
                <Paper key={s.label} elevation={0} sx={{ p: 1.5, borderRadius: '10px', border: `1px solid ${theme.palette.divider}`, flex: 1, textAlign: 'center' }}>
                  <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1.1rem' }}>{s.value ?? 0}</Typography>
                  <Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontSize: '0.68rem' }}>{s.label}</Typography>
                </Paper>
              ))}
            </Box>
          )}

          <Paper elevation={0} sx={{ borderRadius: '12px', border: `1px solid ${theme.palette.divider}`, overflow: 'hidden' }}>
            {/* Header */}
            <Box sx={{ display: 'flex', py: 1.2, px: 2, background: alpha(theme.palette.text.primary, 0.03), borderBottom: `1px solid ${theme.palette.divider}` }}>
              {docHeaders.map((cell) => (
                <Box key={cell.label} sx={{ flex: cell.flex, width: cell.width, textAlign: cell.align || 'left', flexShrink: cell.width ? 0 : undefined }}>
                  <Typography variant="caption" sx={{ fontWeight: 600, color: theme.palette.text.secondary, textTransform: 'uppercase', fontSize: '0.68rem', letterSpacing: '0.05em' }}>
                    {cell.label}
                  </Typography>
                </Box>
              ))}
            </Box>

            {/* Body */}
            {docLoading ? (
              <Box sx={{ p: 2 }}>
                {[...Array(6)].map((_, i) => (
                  <Skeleton key={i} variant="text" width="100%" height={40} sx={{ mb: 0.5 }} />
                ))}
              </Box>
            ) : topUsers.length === 0 ? (
              <Box sx={{ p: 4, textAlign: 'center' }}>
                <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>{t('analytics_no_doc_access_data')}</Typography>
              </Box>
            ) : (
              topUsers.map((u, idx) => (
                <Box
                  key={u.user_id}
                  onClick={() => handleUserClick(u.user_id)}
                  sx={{
                    display: 'flex', py: 1.4, px: 2, cursor: 'pointer', alignItems: 'center',
                    borderBottom: idx < topUsers.length - 1 ? `1px solid ${alpha(theme.palette.divider, 0.5)}` : 'none',
                    transition: 'background 0.15s', '&:hover': { background: alpha(goldColor, 0.04) },
                  }}
                >
                  <Box sx={{ flex: 2, minWidth: 0 }}>
                    <Typography variant="body2" sx={{ fontSize: '0.82rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {u.email}
                    </Typography>
                  </Box>
                  <Box sx={{ flex: 1.5, minWidth: 0 }}>
                    <Typography variant="body2" sx={{ fontSize: '0.78rem', color: theme.palette.text.secondary }}>
                      {u.organization || '-'}
                    </Typography>
                  </Box>
                  <Box sx={{ width: 110, flexShrink: 0, textAlign: 'right' }}>
                    <Typography variant="body2" sx={{ fontSize: '0.82rem', fontWeight: 600 }}>{u.access_count}</Typography>
                  </Box>
                  <Box sx={{ width: 140, flexShrink: 0 }}>
                    <Typography variant="body2" sx={{ fontSize: '0.72rem', color: theme.palette.text.secondary }}>
                      {u.last_access ? new Date(u.last_access).toLocaleString() : '-'}
                    </Typography>
                  </Box>
                </Box>
              ))
            )}
          </Paper>
        </>
      )}

      {/* User Compliance Detail Drawer */}
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        PaperProps={{ sx: { width: 420, maxWidth: '100vw', p: 3, background: theme.palette.background.default } }}
      >
        {complianceLoading ? (
          <Box sx={{ p: 2 }}>
            {[...Array(8)].map((_, i) => (
              <Skeleton key={i} variant="text" width="100%" height={32} sx={{ mb: 1 }} />
            ))}
          </Box>
        ) : userCompliance ? (
          <>
            {/* Header */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  {userCompliance.user?.name || userCompliance.user?.email}
                </Typography>
                <Typography variant="body2" sx={{ color: theme.palette.text.secondary, fontSize: '0.8rem' }}>
                  {userCompliance.user?.email}
                </Typography>
                <Typography variant="caption" sx={{ color: theme.palette.text.disabled }}>
                  {userCompliance.user?.organization || '-'}
                </Typography>
              </Box>
              <IconButton size="small" onClick={() => setDrawerOpen(false)}>
                <X size={18} />
              </IconButton>
            </Box>

            {/* Total Score */}
            <Box sx={{ p: 2.5, borderRadius: '12px', background: alpha(goldColor, 0.05), border: `1px solid ${alpha(goldColor, 0.15)}`, mb: 3, textAlign: 'center' }}>
              <Typography variant="h4" sx={{ fontWeight: 700, color: (userCompliance.score?.total || 0) >= 60 ? '#ef4444' : (userCompliance.score?.total || 0) >= 40 ? '#f59e0b' : theme.palette.text.primary }}>
                {userCompliance.score?.total || 0}
              </Typography>
              <Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontSize: '0.72rem' }}>
                {t('analytics_compliance_score')}
              </Typography>
            </Box>

            {/* Score Breakdown */}
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5 }}>{t('analytics_score_breakdown')}</Typography>
            <ScoreBar label={t('analytics_concurrent_sessions')} value={userCompliance.score?.concurrent_session || 0} max={40} color="#f59e0b" />
            <ScoreBar label={t('analytics_device_rotation')} value={userCompliance.score?.device_rotation || 0} max={30} color="#f97316" />
            <ScoreBar label={t('analytics_usage_volume')} value={userCompliance.score?.usage_volume || 0} max={20} color="#8b5cf6" />
            <ScoreBar label={t('analytics_behavioral_fingerprint')} value={userCompliance.score?.behavioral_fingerprint || 0} max={15} color="#06b6d4" />

            {/* Actions */}
            {userCompliance.user?.suspended_until && (
              <Box sx={{ mt: 2, mb: 2 }}>
                <Button
                  size="small"
                  variant="outlined"
                  color="warning"
                  onClick={() => handleUnsuspend(userCompliance.user.id)}
                  startIcon={<CheckCircle size={14} />}
                  sx={{ borderRadius: '8px', textTransform: 'none', fontSize: '0.78rem' }}
                >
                  {t('analytics_unsuspend_user')}
                </Button>
                <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: theme.palette.text.disabled }}>
                  Suspended until {new Date(userCompliance.user.suspended_until).toLocaleString()}
                </Typography>
              </Box>
            )}

            {/* Violations History */}
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mt: 3, mb: 1.5 }}>{t('analytics_violation_history')}</Typography>
            {(userCompliance.violations || []).length === 0 ? (
              <Typography variant="body2" sx={{ color: theme.palette.text.secondary, fontSize: '0.8rem' }}>{t('analytics_no_violations_history')}</Typography>
            ) : (
              (userCompliance.violations || []).map((v) => (
                <Box key={v.id} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, p: 1.5, borderRadius: '8px', border: `1px solid ${theme.palette.divider}` }}>
                  <Box sx={{ flex: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.3 }}>
                      <Chip label={v.severity} size="small" sx={{ height: 18, fontSize: '0.65rem', fontWeight: 600, background: alpha(SEVERITY_COLORS[v.severity] || goldColor, 0.12), color: SEVERITY_COLORS[v.severity] || goldColor, border: 'none' }} />
                      <Typography variant="caption" sx={{ fontSize: '0.7rem', color: theme.palette.text.secondary }}>
                        {v.violation_type?.replace(/_/g, ' ')}
                      </Typography>
                    </Box>
                    <Typography variant="caption" sx={{ color: theme.palette.text.disabled, fontSize: '0.68rem' }}>
                      {v.created_at ? new Date(v.created_at).toLocaleString() : '-'} | Score: {v.score}
                    </Typography>
                  </Box>
                  {!v.resolved && (
                    <IconButton
                      size="small"
                      onClick={(e) => { e.stopPropagation(); handleResolve(userCompliance.user?.id, v.id) }}
                      sx={{ color: '#22c55e' }}
                      title="Resolve"
                    >
                      <CheckCircle size={16} />
                    </IconButton>
                  )}
                </Box>
              ))
            )}

            {/* Devices */}
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mt: 3, mb: 1.5 }}>{t('analytics_active_devices')}</Typography>
            {(userCompliance.devices || []).length === 0 ? (
              <Typography variant="body2" sx={{ color: theme.palette.text.secondary, fontSize: '0.8rem' }}>{t('analytics_no_active_devices')}</Typography>
            ) : (
              (userCompliance.devices || []).map((d) => (
                <Box key={d.id} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
                  <Monitor size={16} style={{ color: theme.palette.text.secondary, flexShrink: 0 }} />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body2" sx={{ fontSize: '0.8rem', fontWeight: 500 }}>{d.name}</Typography>
                    <Typography variant="caption" sx={{ color: theme.palette.text.disabled, fontSize: '0.68rem', display: 'block' }}>
                      {d.ip_address || '-'} | {d.device_type || '-'} | Last: {d.last_active ? new Date(d.last_active).toLocaleString() : '-'}
                    </Typography>
                  </Box>
                </Box>
              ))
            )}
          </>
        ) : null}
      </Drawer>
    </Box>
  )
}

export default SecurityTab
